import { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { areaService } from '@/services/areaService';
import { projectService } from '@/services/projectService';
import { useTheme } from '@/hooks/useTheme';
import { useAuthStore } from '@/store/authStore';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { Toast } from '@/components/Toast';

interface ImportSummary {
  buildings: {
    created: number;
    items: Array<{ name: string; code: string }>;
  };
  floors: {
    created: number;
    items: Array<{ name: string; code: string; parent: string }>;
  };
  appartements: {
    created: number;
    total?: number;
    unique?: number;
    total_occurrences?: number;
    items: Array<{ name: string; code: string; number: string; parent: string }>;
    byFloor?: Record<string, { total: number; created: number; existing: number }>;
  };
  rooms?: {
    created: number;
    items: Array<{ name: string; code: string; parent: string }>;
  };
  boxes?: {
    created: number;
    items: Array<{ uuid: string; name: string }>;
  };
  devices?: {
    created: number;
    items: Array<{ uuid: string; name: string }>;
  };
  attributes?: {
    created: number;
    items: Array<{ name: string; device: string }>;
  };
  total_lines?: number;
  errors: string[];
}

interface TreeNode {
  name: string;
  code: string;
  number?: string;
  children: TreeNode[];
}

export function ImportAreasPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isDark } = useTheme();
  const { user } = useAuthStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<ImportSummary | null>(null);

  const [importError, setImportError] = useState<{ title: string; details?: string } | null>(null);
  const [importSuccess, setImportSuccess] = useState<string | null>(null);

  // Fetch project
  const {
    data: project,
    isLoading: projectLoading,
    isError: projectError,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectService.getProject(Number(projectId)),
    enabled: !!projectId,
  });

  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showToast, setShowToast] = useState(false);

  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!projectId) throw new Error('Project ID is required');
      return areaService.importAreas(Number(projectId), file);
    },
    onSuccess: (data) => {
      // Debug: log the received data structure
      console.log('Import response data:', data);
      console.log('Appartements byFloor:', data.summary?.appartements?.byFloor);
      
      setImportError(null);
      setImportSummary(data.summary);
      
      const totalLines = data.summary.total_lines || (data.summary.appartements.total ?? data.summary.appartements.created);
      const uniqueAppartements = data.summary.appartements.unique ?? 0;
      const createdAppartements = data.summary.appartements.created;
      
      let successMessage = `Import réussi ! ${totalLines} lignes traitées.`;
      successMessage += ` ${data.summary.buildings.created} bâtiments, ${data.summary.floors.created} étages.`;
      successMessage += ` ${uniqueAppartements} appartements uniques (${createdAppartements} créés).`;
      
      setImportSuccess(successMessage);
      
      setToastMessage('Import terminé avec succès');
      setToastType('success');
      setShowToast(true);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      // Invalidate areas queries to refresh the data
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
        queryClient.invalidateQueries({ queryKey: ['areas'] });
      }
    },
    onError: (error: any) => {
      const errorData = error?.response?.data;
      const title = errorData?.error || error?.message || 'Erreur lors de l\'import';
      const details = errorData?.details;

      setImportError({ title, details });
      setImportSuccess(null);
      
      if (errorData?.summary) {
        setImportSummary(errorData.summary);
      } else {
        setImportSummary(null);
      }

      setToastMessage(title);
      setToastType('error');
      setShowToast(true);
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        setToastMessage('Veuillez sélectionner un fichier CSV');
        setToastType('error');
        setShowToast(true);
        return;
      }
      setSelectedFile(file);
      setImportSummary(null);
      setImportError(null);
      setImportSuccess(null);
    }
  };

  const handleImport = () => {
    if (!selectedFile) {
      setToastMessage('Veuillez sélectionner un fichier');
      setToastType('error');
      setShowToast(true);
      return;
    }
    setImportError(null);
    setImportSuccess(null);
    importMutation.mutate(selectedFile);
  };

  const buildTree = (summary: ImportSummary): TreeNode[] => {
    const tree: TreeNode[] = [];
    const buildingMap = new Map<string, TreeNode>();

    // Add buildings
    summary.buildings.items.forEach((building) => {
      const node: TreeNode = {
        name: building.name,
        code: building.code,
        children: [],
      };
      buildingMap.set(building.code, node);
      tree.push(node);
    });

    // Add floors
    summary.floors.items.forEach((floor) => {
      const building = buildingMap.get(floor.code.split('-ETAGE-')[0]);
      if (building) {
        const floorNode: TreeNode = {
          name: floor.name,
          code: floor.code,
          children: [],
        };
        building.children.push(floorNode);
        buildingMap.set(floor.code, floorNode);
      }
    });

    // Add appartements
    summary.appartements.items.forEach((appart) => {
      const floorCode = appart.code.split('-APP-')[0];
      const floor = buildingMap.get(floorCode);
      if (floor) {
        const appartNode: TreeNode = {
          name: appart.name,
          code: appart.code,
          number: appart.number,
          children: [],
        };
        floor.children.push(appartNode);
      }
    });

    // Add rooms (if any)
    if (summary.rooms) {
      summary.rooms.items.forEach((room) => {
        // Find parent appartement
        // Room code is {APPART_CODE}-{ROOM_NAME}
        // Appart code is the prefix before the last dash
        // But simpler: iterate over all buildings -> floors -> apparts to find the parent
        // Or assume room.parent is the parent name, but we need the node
        
        // Let's search in the tree
        for (const building of tree) {
          for (const floor of building.children) {
            for (const appart of floor.children) {
              if (appart.name === room.parent) {
                const roomNode: TreeNode = {
                  name: room.name,
                  code: room.code,
                  children: [],
                };
                appart.children.push(roomNode);
              }
            }
          }
        }
      });
    }

    return tree;
  };

  const renderTree = (nodes: TreeNode[], level = 0): JSX.Element[] => {
    return nodes.map((node, index) => (
      <div key={`${node.code}-${index}`} className="mb-2">
        <div
          className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
            level === 0
              ? 'bg-primary/10 text-primary dark:bg-primary/20'
              : level === 1
              ? 'bg-slate-100 dark:bg-gray-800'
              : 'bg-slate-50 dark:bg-gray-900'
          }`}
          style={{ paddingLeft: `${level * 24 + 12}px` }}
        >
          <span className="material-icons-outlined text-sm">
            {level === 0 ? 'domain' : level === 1 ? 'layers' : 'home'}
          </span>
          <span className="flex-1 text-sm font-medium">{node.name}</span>
          {node.number && (
            <span className="text-xs text-slate-500 dark:text-slate-400">#{node.number}</span>
          )}
          <span className="text-xs text-slate-400 dark:text-slate-500 font-mono">{node.code}</span>
        </div>
        {node.children.length > 0 && <div className="ml-4">{renderTree(node.children, level + 1)}</div>}
      </div>
    ));
  };

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (!projectId) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Project ID manquant</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  if (projectLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-500 dark:text-slate-400">Chargement...</p>
        </div>
      </div>
    );
  }

  if (projectError || !project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-[#0B1116]">
        <div className="text-center">
          <p className="text-red-500">Projet non trouvé</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-4 text-primary hover:text-primary-600"
          >
            Retour au dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      {/* Sidebar */}
      <AdminSidebar getUserInitials={getUserInitials} />

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        {/* Header */}
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/admin/projects/${projectId}/areas`)}
              className="p-2 text-slate-400 hover:text-slate-500 dark:hover:text-white transition-colors"
            >
              <span className="material-icons-outlined">arrow_back</span>
            </button>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              <span className="text-slate-400 dark:text-slate-600">Admin</span> / Import Zones
            </div>
            {project && (
              <div className="flex items-center gap-2">
                <span className="text-lg font-bold text-slate-900 dark:text-white">{project.title}</span>
                {project.city && (
                  <>
                    <span className="text-slate-400">•</span>
                    <span className="text-sm text-slate-500 dark:text-slate-400">{project.city}</span>
                  </>
                )}
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            {/* Instructions */}
            <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] p-6 mb-6">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                <span className="material-icons-outlined">info</span>
                Instructions d'import
              </h2>
              <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
                <p>Le fichier CSV doit contenir les colonnes suivantes :</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>bat</strong> : Numéro du bâtiment (ex: 30)</li>
                  <li><strong>etage</strong> : Nom de l'étage (ex: R+2)</li>
                  <li><strong>appart</strong> : Numéro de l'appartement (ex: 2105)</li>
                  <li><strong>room</strong> (optionnel) : Nom de la pièce (ex: Cuisine)</li>
                  <li><strong>box, box_code, box_uuid</strong> (optionnels) : Infos de la box</li>
                  <li><strong>device, device_uuid, device_type</strong> (optionnels) : Infos de l'équipement</li>
                  <li><strong>attribute, attribute_uuid, attribute_type</strong> (optionnels) : Infos de l'attribut</li>
                </ul>
                <p className="mt-4">
                  <strong>Format attendu :</strong> bat,etage,appart,room,box,box_code,box_uuid,device,device_uuid,device_type,attribute,attribute_uuid,attribute_type
                </p>
                <div className="mt-4 p-3 bg-slate-50 dark:bg-[#0B1116] rounded-lg font-mono text-xs overflow-x-auto">
                  30,R+2,2105,Cuisine,Box1,CODE1,uuid-box,Thermo1,uuid-dev,Thermostat,Temp,uuid-attr,Temperature
                </div>
              </div>
            </div>

            {/* Success Display */}
            {importSuccess && (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-200 dark:border-green-800 p-6 mb-6">
                <h2 className="text-lg font-bold text-green-800 dark:text-green-400 mb-2 flex items-center gap-2">
                  <span className="material-icons-outlined">check_circle</span>
                  Succès
                </h2>
                <p className="text-sm text-green-700 dark:text-green-300">
                  {importSuccess}
                </p>
              </div>
            )}

            {/* Error Display */}
            {importError && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-6 mb-6">
                <h2 className="text-lg font-bold text-red-800 dark:text-red-400 mb-2 flex items-center gap-2">
                  <span className="material-icons-outlined">error</span>
                  {importError.title}
                </h2>
                {importError.details && (
                  <div className="mt-2">
                    <p className="text-sm text-red-700 dark:text-red-300 mb-1">Détails techniques :</p>
                    <pre className="text-xs font-mono bg-red-100 dark:bg-red-950/50 p-3 rounded overflow-x-auto text-red-800 dark:text-red-200 whitespace-pre-wrap">
                      {importError.details}
                    </pre>
                    {importError.details.includes('uniq_device_box_code') && (
                       <p className="mt-3 text-sm font-medium text-red-800 dark:text-red-300 bg-red-100 dark:bg-red-900/30 p-2 rounded">
                         <span className="font-bold">Cause probable :</span> Un équipement (device) existe déjà avec le même code sur cette box, mais avec un UUID différent ou manquant. Vérifiez les doublons dans votre fichier ou la base de données.
                       </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* File Upload */}
            <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] p-6 mb-6">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Sélectionner le fichier CSV</h2>
              <div className="space-y-4">
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={handleFileSelect}
                    className="block w-full text-sm text-slate-500 dark:text-slate-400
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-primary file:text-white
                      hover:file:bg-sky-600
                      file:cursor-pointer
                      dark:file:bg-primary dark:file:text-white"
                  />
                </div>
                {selectedFile && (
                  <div className="p-3 bg-slate-50 dark:bg-[#0B1116] rounded-lg">
                    <div className="flex items-center gap-2">
                      <span className="material-icons-outlined text-primary">description</span>
                      <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedFile.name}</span>
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        ({(selectedFile.size / 1024).toFixed(2)} KB)
                      </span>
                    </div>
                  </div>
                )}
                <button
                  onClick={handleImport}
                  disabled={!selectedFile || importMutation.isPending}
                  className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {importMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <span className="material-icons-outlined">upload</span>
                      Importer
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Summary */}
            {importSummary && (
              <div className="bg-white dark:bg-[#16202A] rounded-xl border border-slate-200 dark:border-[#1E293B] p-6">
                <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
                  <span className="material-icons-outlined">check_circle</span>
                  Résumé de l'import
                </h2>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 bg-primary/10 dark:bg-primary/20 rounded-lg">
                    <div className="text-2xl font-bold text-primary">{importSummary.buildings.created}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Bâtiments créés</div>
                  </div>
                  <div className="p-4 bg-sky-500/10 dark:bg-sky-500/20 rounded-lg">
                    <div className="text-2xl font-bold text-sky-600 dark:text-sky-400">
                      {importSummary.floors.created}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">Étages créés</div>
                  </div>
                  <div className="p-4 bg-green-500/10 dark:bg-green-500/20 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                      {importSummary.appartements.unique ?? importSummary.appartements.created}
                    </div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">
                      Appartements uniques
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      ({importSummary.appartements.created} créés)
                    </div>
                  </div>
                  
                  {importSummary.rooms && (
                    <div className="p-4 bg-purple-500/10 dark:bg-purple-500/20 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                        {importSummary.rooms.created}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Pièces créées</div>
                    </div>
                  )}

                  {importSummary.boxes && (
                    <div className="p-4 bg-orange-500/10 dark:bg-orange-500/20 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                        {importSummary.boxes.created}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Box créées</div>
                    </div>
                  )}

                  {importSummary.devices && (
                    <div className="p-4 bg-teal-500/10 dark:bg-teal-500/20 rounded-lg">
                      <div className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                        {importSummary.devices.created}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Devices créés</div>
                    </div>
                  )}

                  {importSummary.attributes && (
                    <div className="p-4 bg-pink-500/10 dark:bg-pink-500/20 rounded-lg">
                      <div className="text-2xl font-bold text-pink-600 dark:text-pink-400">
                        {importSummary.attributes.created}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-slate-400">Attributs créés</div>
                    </div>
                  )}
                </div>

                {/* Statistics by Floor */}
                {importSummary.appartements.byFloor && Object.keys(importSummary.appartements.byFloor).length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 uppercase tracking-wide">
                      Détails par étage
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {Object.entries(importSummary.appartements.byFloor)
                        .sort(([a], [b]) => {
                          // Sort floors by name (R+2, R+5, R+8, etc.)
                          return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
                        })
                        .map(([floorName, stats]) => {
                          // Ensure stats values are defined
                          const total = stats?.total ?? 0;
                          const created = stats?.created ?? 0;
                          const existing = stats?.existing ?? 0;
                          
                          return (
                            <div key={floorName} className="p-3 bg-slate-50 dark:bg-[#0B1116] rounded-lg border border-slate-200 dark:border-[#1E293B]">
                              <div className="text-sm font-medium text-slate-900 dark:text-white mb-2">{floorName}</div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">
                                Total: <span className="font-semibold">{total}</span> | 
                                Créés: <span className="font-semibold text-green-600 dark:text-green-400">{created}</span>
                                {existing > 0 && (
                                  <> | Existants: <span className="font-semibold text-slate-500">{existing}</span></>
                                )}
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}

                {importSummary.errors.length > 0 && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                    <h3 className="text-sm font-semibold text-red-800 dark:text-red-400 mb-2">
                      Problème lors de l'import ({importSummary.errors.length} erreurs)
                    </h3>
                    <ul className="list-disc list-inside space-y-1 text-sm text-red-700 dark:text-red-300 max-h-60 overflow-y-auto">
                      {importSummary.errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tree View */}
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 uppercase tracking-wide">
                    Structure créée
                  </h3>
                  <div className="max-h-96 overflow-y-auto p-4 bg-slate-50 dark:bg-[#0B1116] rounded-lg">
                    {renderTree(buildTree(importSummary))}
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={async () => {
                      // Ensure queries are invalidated before navigation
                      if (projectId) {
                        await queryClient.invalidateQueries({ queryKey: ['areas', projectId] });
                        await queryClient.invalidateQueries({ queryKey: ['areas'] });
                      }
                      navigate(`/admin/projects/${projectId}/areas`);
                    }}
                    className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-sky-600 transition-colors flex items-center gap-2"
                  >
                    <span className="material-icons-outlined text-sm">visibility</span>
                    Voir les zones
                  </button>
                  <button
                    onClick={() => {
                      setImportSummary(null);
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="px-4 py-2 border border-slate-300 dark:border-[#1E293B] rounded-lg text-slate-700 dark:text-slate-200 bg-white dark:bg-[#16202A] hover:bg-slate-50 dark:hover:bg-gray-800 transition-colors"
                  >
                    Nouvel import
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={showToast}
        onClose={() => setShowToast(false)}
      />
    </div>
  );
}
